import { JSX, useMemo, useState, useRef } from 'react';
import {
    useRecordContext
} from 'react-admin';
import { useFormContext, useWatch } from "react-hook-form"
import Editor from '@monaco-editor/react';
import DOMPurify from 'dompurify';
import {
    Grid,
    Box,
    Drawer
} from '@mui/material';
import { hostUrl } from "../utils/url-manager";
import { TemplateVariable, applyTemplateSamples } from "./components/template-variables";
import { buildNokTemplateSamples, variablesForType } from "./nok-template-variables";
import { useASettingByCode } from "../global-hooks/default-system-hooks";

export const NokEmailTemplateCodeEditor = (props: any) => {
    const record = useRecordContext();
    const { setValue } = useFormContext();
    const [html, setHtml] = useState<string>(record?.html ?? '');
    const [showPreview, setShowPreview] = useState<boolean>(false);
    const previewRef = useRef<HTMLDivElement>(null);
    const editorContainerRef = useRef<HTMLDivElement>(null);
    // The variables depend on the template type chosen above the editor, and follow it.
    const templateType: string | undefined = useWatch({ name: 'type' });
    const variables: TemplateVariable[] = variablesForType(templateType);
    // A Compliance Sniffer body's {{signature}} previews as the real stored signature.
    const { settingValue: complianceSignature } = useASettingByCode('default_compliance_sniffer_email_signature');

    // A token this type does not fill is sent to the recipient as written, so say so.
    const unknownTokens: string[] = useMemo((): string[] => {
        const known: Set<string> = new Set(variables.map((variable: TemplateVariable): string => variable.token));
        const found: string[] = (html.match(/\{\{\s*[a-zA-Z0-9_]+\s*\}\}/g) ?? [])
            .map((token: string): string => token.replace(/\s/g, ''));
        return Array.from(new Set(found)).filter((token: string): boolean => !known.has(token));
    }, [html, variables]);

    function setHtmlContent(value: string, event): void {
        let cleanHtml: string  = DOMPurify.sanitize(
            value,
            {
                WHOLE_DOCUMENT: true,
                ADD_TAGS: ['head', 'meta', 'body', 'title', 'DOCTYPE']
            }
        );
        setHtml(cleanHtml);
        setValue('html', cleanHtml, { shouldDirty: true } );
    }

    function handleShowPreviewClick(): void {
        setShowPreview(!showPreview);
    }

    /** The template with this type's variables filled with sample data. */
    function parseHtmlVariables(): string {
        return applyTemplateSamples(html, buildNokTemplateSamples(templateType, hostUrl, complianceSignature));
    }

    return (
        <>
            <Grid container spacing={2} sx={ { height: '400px', width: '100%' } }>
                <Grid size={6}>
                    <p>{ "You can use the field below to add inline styled HTML for this email template." }</p>
                    <p>{ "Use the variables to inject various personnel and/or company data into your HTML." }</p>
                    <p>{ "The HTML is sanitized so script tags will be removed." }</p>
                    <p>{ "Note: Remember to account for Outlook and other oddities with email HTML." }</p>
                </Grid>
                <Grid size={6}>
                    { (variables.length === 0) &&
                        <p>{ "HQ-API fills no variables in this type of template." }</p>
                    }
                    { variables.map((variable: TemplateVariable): JSX.Element => (
                        <p key={ variable.token }>
                            <Box component="code" sx={ { fontFamily: 'monospace' } }>{ variable.token }</Box>
                            { ' - ' + variable.description }
                        </p>
                    )) }
                    { (unknownTokens.length > 0) &&
                        <p>
                            { 'Also used here: ' }
                            <Box component="code" sx={ { fontFamily: 'monospace', color: 'warning.main' } }>{ unknownTokens.join('  ') }</Box>
                            { '. Nothing fills these in this type of template, so they are sent as written.' }
                        </p>
                    }
                </Grid>
                <Grid size={12}>
                    <Box sx={ {
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'flex-end'
                    } }>
                        <button type="button"
                                onClick={ handleShowPreviewClick }
                                style={ { cursor: 'pointer' } }
                                title="Show Preview">
                            <span>{ "Preview HTML" }</span>
                        </button>
                    </Box>
                </Grid>
            </Grid>
            <Box sx={ {
                width: '100%',
                border: '1px solid', borderColor: 'divider',
                display: 'flex',
                // flexDirection: 'column',
            } }>
                <Box ref={ editorContainerRef }
                    sx={ {
                    width: '100%',
                    height: '500px',
                    overflowY: 'auto',
                    // display: 'flex',
                    // flexDirection: 'column',
                } }>
                    <Editor
                        defaultValue={ html }
                        height="100%"
                        defaultLanguage="html"
                        onChange={ setHtmlContent }
                    />

                </Box>
                <Drawer open={ showPreview } onClose={ handleShowPreviewClick } >
                    <Box ref={ previewRef } sx={ {
                        width: '100%',
                        height: '100%',
                        background: '#fff',
                    } }>
                        { /* Block, as a mail client lays it out: flex put a body template's paragraphs side by side. */ }
                        <div style={ { width: '100%', maxWidth: '100%', display: 'block', color: 'black', padding: '16px' } } dangerouslySetInnerHTML={ { __html: parseHtmlVariables() } }  />
                    </Box>
                </Drawer>


             </Box>


        </>

    );
}